import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Email, ArrowBack } from '@mui/icons-material';
import { authAPI, tenantSettingsAPI } from '@/services/api';

// Default styling constants
const DEFAULT_BG_COLOR = '#4a90e2';
const DEFAULT_LOGO_URL = 'https://rallyon.app/logo192.png';
const DEFAULT_HERO_URL = 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1675&q=80';
const DEFAULT_TITLE = 'RallyOn';
const DEFAULT_SUBTITLE = 'Streamline your club operations and enhance member experience';

// Validation schema
const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState<string>(DEFAULT_SUBTITLE);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO_URL);
  const [heroUrl, setHeroUrl] = useState<string>(DEFAULT_HERO_URL);
  const [bgColor, setBgColor] = useState<string>(DEFAULT_BG_COLOR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Fetch public tenant settings to customize the page
    async function fetchSettings() {
      if (import.meta.env.VITE_DEPLOYMENT_MODE === 'standalone' && import.meta.env.VITE_TENANT_NAME) {
        try {
          const response = await tenantSettingsAPI.getPublicSettings(import.meta.env.VITE_TENANT_NAME);
          
          const background_colour = response?.data?.tenant_background_colour || DEFAULT_BG_COLOR;
          const logo_url = response?.data?.tenant_logo_url || DEFAULT_LOGO_URL;
          const hero_url = response?.data?.tenant_hero_url || DEFAULT_HERO_URL;
          const title = response?.data?.tenant_heading_title || DEFAULT_TITLE;
          const subtitle = response?.data?.tenant_heading_subtitle || DEFAULT_SUBTITLE;

          setBgColor(background_colour);
          setLogoUrl(logo_url);
          setHeroUrl(hero_url);
          setTitle(title);
          setSubtitle(subtitle);
        } catch (error) {
          console.error('Error fetching tenant settings:', error);
        }
      } 
    }

    fetchSettings();
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess(false);

      try {
        // Include tenant name for standalone deployments
        const tenantName = import.meta.env.VITE_DEPLOYMENT_MODE === 'standalone' ? 
          import.meta.env.VITE_TENANT_NAME : undefined;

        await authAPI.forgotPassword(values.email, tenantName);
        setSuccess(true);
      } catch (err: any) {
        let errorMessage = 'Failed to send reset email. Please try again.';

        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: bgColor ? `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}CC 100%)` : 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ 
          width: '100%', 
          maxWidth: { xs: '100%', sm: '500px', md: '800px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Left side - Banner/Image (hidden on mobile) */}
          {!isMobile && (
            <Box sx={{ flex: '1', p: 4, color: 'white', textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" mb={3}>
                {title}
              </Typography>
              <Typography variant="h6" mb={4}>
                {subtitle}
              </Typography>
              <Box
                component="img"
                src={heroUrl || DEFAULT_HERO_URL}
                alt="Hero Image"
                sx={{
                  width: '90%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '20px',
                  boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
                }}
              />
            </Box>
          )}

          {/* Right side - Forgot Password Form */}
          <Box sx={{ flex: '1' }}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Back to Login Link */}
              <Box sx={{ mb: 2 }}>
                <Link 
                  component={RouterLink} 
                  to="/login" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
                  Back to Login
                </Link>
              </Box>

              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  Forgot Password?
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  {success ? 
                    'Check your email for reset instructions' : 
                    'Enter your email address and we\'ll send you a link to reset your password'
                  }
                </Typography>
              </Box>

              {success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    If an account with that email exists, a password reset link has been sent.
                    Please check your email and follow the instructions to reset your password.
                  </Alert>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Didn't receive an email? Check your spam folder or try again with a different email address.
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSuccess(false);
                      setError('');
                      formik.resetForm();
                    }}
                    sx={{ mr: 2 }}
                  >
                    Try Again
                  </Button>

                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/login"
                  >
                    Back to Login
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={formik.handleSubmit}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box mb={3}>
                    <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                      Email Address*
                    </Typography>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      placeholder="Enter your email address"
                      autoComplete="email"
                      autoFocus
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      disabled={loading}
                      InputProps={{
                        startAdornment: <Email color="action" sx={{ mr: 1 }} />,
                        sx: {
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      boxShadow: '0 4px 7px -1px rgba(0,0,0,0.11), 0 2px 4px -1px rgba(0,0,0,0.07)',
                      mb: 3
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Remember your password?{' '}
                      <Link component={RouterLink} to="/login" color="primary">
                        Sign in here
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;